// LoginForm.tsx 
// прямые вызовы к API авторизации, без посредников. 
// В случае успеха вызывает onSuccess
// переходы в машине состояний:
// - логин успешен -> AUTHORIZED (вызывается onSuccess, например, для axios)
// - логин неуспешен -> LOGIN (с ошибкой)
// - клик на "забыли пароль" -> FORGOT_PASSWORD
import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  FormControl,
  InputAdornment,
  IconButton,
  Link,
} from "@mui/material";
import { useTranslation } from 'react-i18next';
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { PasswordTextField } from "../userfront/PasswordTextField";
import { useAuthStore } from "../../store/useAuthStore3";

interface LoginState {
  username: string;
  password: string;
}

interface LoginFormProps {
  user?: string;
  passwd?: string;
  onSuccess?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ user, passwd, onSuccess }) => {
  const { t } = useTranslation();
  
  // Достаем всё необходимое из стора (включая loginViaGoogle)
  const { error: globalError, login, loginViaGoogle, accessToken } = useAuthStore();

  const [formData, setFormData] = useState<LoginState>({
    username: user || "",
    password: passwd || "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Сброс ошибок при размонтировании компонента, чтобы не спамить в других экранах FSM
  useEffect(() => {
    return () => {
      useAuthStore.setState({ error: null });
    };
  }, []);

  // Если токен уже есть, форму не показываем
  // if (accessToken) return null;

  const handleClickShowPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    setShowPassword((show) => !show);
    event.currentTarget.blur();
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Стандартный логин
  const handleSubmit = async (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    setIsLoading(true);
    useAuthStore.setState({ error: null }); // Сбрасываем прошлую ошибку перед новым запросом

    try {
      const res = await login(formData.username, formData.password);
      console.log('login succeeded', res);
      onSuccess?.();
      if (useAuthStore.getState().isConfirmed) {
        useAuthStore.setState({ authState: 'AUTHORIZED' });
      } else {
        useAuthStore.setState({ authState: 'REGISTERED' });
      }
    } catch (error: any) {
      console.error('login failed', error);
      useAuthStore.setState({ 
        authState: 'LOGIN', 
        error: error?.message || t('LoginForm.DefaultError') 
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Логин через Google
  const handleGoogleClick = async () => {
    setIsLoading(true);
    useAuthStore.setState({ error: null });

    try {
      // Проверяем, что метод существует в сторе
      if (!loginViaGoogle) throw new Error("Google auth method is not implemented in store");
      
      const url = await loginViaGoogle();
      window.location.href = url;
      console.log('Google redirect succeeded', url);

      
      
      // useAuthStore.setState({ authState: 'AUTHORIZED' });
      // onSuccess?.();
    } catch (error: any) {
      console.error('Google login failed', error);
      useAuthStore.setState({ 
        authState: 'LOGIN', 
        error: error?.message || t('LoginForm.DefaultError') 
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        maxWidth: 400,
        mx: "auto",
        p: 3,
        border: "1px solid #ccc",
        borderRadius: "8px",
      }}
    >
      <Typography variant="h4" component="h1" align="center">
        {t('LoginForm.Login')}
      </Typography>
      
      {globalError && (
        <Typography color="error" align="center" sx={{ fontWeight: "bold" }}>
          {globalError}
        </Typography>
      )}      
      
      <FormControl fullWidth>
        <TextField
          label={t('LoginForm.Email')}
          name="username"
          type="text"
          value={formData.username}
          onChange={handleInputChange}
          disabled={isLoading}
          required
        />
      </FormControl>

      <FormControl fullWidth>
        <PasswordTextField
          label={t('LoginForm.Password')}
          name="password"
          type={showPassword ? "text" : "password"}
          value={formData.password}
          onChange={handleInputChange}
          disabled={isLoading}
          required
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                  disabled={isLoading}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </FormControl>

      <Button 
        type="submit" 
        variant="contained" 
        disabled={isLoading}
      >
        {isLoading ? t('LoginForm.Loading') : t('LoginForm.Login')}
      </Button>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={() => useAuthStore.setState({ authState: 'FORGOT_PASSWORD' })}
          disabled={isLoading}
          underline="hover"
        >
          {t('LoginForm.forgot_password')}
        </Link>
      </Box>

      <Button 
        variant="outlined" 
        onClick={handleGoogleClick}
        disabled={isLoading}
        fullWidth
      >
        Вхід через Google
      </Button>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={() => useAuthStore.setState({ authState: 'NOT_REGISTERED' })}
          disabled={isLoading}
          underline="hover"
        >
          {'Якшо не зареєстровані, перейдіть до реєстрації'}
        </Link>
      </Box>

    </Box>
  );
};

export default LoginForm;